import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Camera, ListBullets, MapPin, MapTrifold } from "@phosphor-icons/react";
import { PlanAnsicht, Verorten } from "../../components/Lageplan";
import { aender, lies, schick } from "../../api/client";
import type { Leistung, Mangel, MangelStatus, Projekt, Schreiben } from "../../api/types";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { FotoLeiste } from "../../components/FotoLeiste";
import { VersandDialog } from "../../components/Versand";
import { MANGEL_STATUS, MANGEL_UEBERGAENGE, datum, heute } from "../../lib/format";
import { Auswahl } from "../../components/Auswahl";
import { Diktat } from "../../components/Diktat";
import { ohneNetz, vormerken } from "../../lib/warteschlange";

const FARBE: Record<MangelStatus, string> = {
  offen: "fehler", angezeigt: "warn", beseitigt_gemeldet: "akzent", erledigt: "gut", strittig: "fehler", entfallen: "",
};

export function useLeistungen(projektId: string | undefined) {
  return useQuery({
    queryKey: ["leistungen", projektId],
    queryFn: () => lies<Leistung[]>(`/projekte/${projektId}/leistungen`),
    // `undefined`, solange eine Seite das Projekt erst aus dem Dokument erfaehrt
    // (Protokoll) — ohne die Bremse ging GET /projekte//leistungen raus.
    enabled: !!projektId,
  });
}

export function MaengelReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const [nurOffen, setNurOffen] = useState(true);
  const [leistung, setLeistung] = useState("");
  const [neu, setNeu] = useState(false);
  const [auswahl, setAuswahl] = useState<Mangel | null>(null);
  const [ansicht, setAnsicht] = useState<"liste" | "plan">("liste");
  const leistungen = useLeistungen(projekt.id);
  const qc = useQueryClient();
  const [anzeigeSenden, setAnzeigeSenden] = useState(false);
  // E12: die Anzeige geht als Ausgangsschreiben raus (E10); vorher gab es nur die Vorschau,
  // und „angezeigt“ wurde von Hand gesetzt — ohne Dokument dazu.
  const anzeigen = useMutation({
    mutationFn: (lid: string) => schick<Schreiben>(`/projekte/${projekt.id}/leistungen/${lid}/maengelanzeige`),
    onSuccess: () => { for (const k of [["maengel"], ["schreiben", projekt.id], ["leistungen"]]) qc.invalidateQueries({ queryKey: k }); },
  });
  const maengel = useQuery({
    queryKey: ["maengel", projekt.id, nurOffen, leistung],
    queryFn: () => lies<Mangel[]>(`/projekte/${projekt.id}/maengel?offen=${nurOffen}`
      + (leistung ? `&leistung_id=${leistung}` : "")),
  });

  return (
    <section className="karte">
      <div className="karte-kopf" style={{ flexWrap: "wrap" }}>
        <h2>Mängelliste</h2>
        <div className="wahl" role="group" aria-label="Ansicht">
          <button className={ansicht === "liste" ? "an" : ""} onClick={() => setAnsicht("liste")}><ListBullets size={16} /> Liste</button>
          <button className={ansicht === "plan" ? "an" : ""} onClick={() => setAnsicht("plan")}><MapTrifold size={16} /> Lageplan</button>
        </div>
        <div className="luft" />
        <Auswahl style={{ width: "auto" }} value={leistung} onChange={(e) => setLeistung(e.target.value)}>
          <option value="">alle Leistungen</option>
          {leistungen.data?.map((l) => <option key={l.id} value={l.id}>{l.bezeichnung}</option>)}
        </Auswahl>
        <label className="haken"><input type="checkbox" checked={nurOffen} onChange={(e) => setNurOffen(e.target.checked)} /> nur offene</label>
        {kann && <button className="knopf haupt" onClick={() => setNeu(true)}><Plus size={16} weight="bold" /> Mangel</button>}
      </div>
      {leistung && kann && (
        <p className="mute">
          Mängelanzeige an den Auftragnehmer:{" "}
          <a className="knopf leise" href={`/api/projekte/${projekt.id}/leistungen/${leistung}/maengelanzeige`} target="_blank" rel="noreferrer">
            Vorschau
          </a>{" "}
          {projekt.meine_rolle === "leitung" && (
            <button className="knopf" disabled={anzeigen.isPending}
                    onClick={() => confirm("Mängelanzeige jetzt erzeugen und im Ausgang ablegen? Offene Mängel dieser Leistung gelten danach als angezeigt.") && anzeigen.mutate(leistung)}>
              Mängelanzeige ablegen
            </button>
          )}
          {anzeigen.data && (
            <>
              <span className="gespeichert"> im Ausgang abgelegt </span>
              <button className="knopf haupt" onClick={() => setAnzeigeSenden(true)}>An den Auftragnehmer senden</button>
            </>
          )}
        </p>
      )}
      {anzeigeSenden && anzeigen.data && (
        <VersandDialog schreibenId={anzeigen.data.id} titel={anzeigen.data.titel} zu={() => setAnzeigeSenden(false)} />
      )}
      <Meldung fehler={anzeigen.error} />
      <Meldung fehler={maengel.error} />
      {ansicht === "plan" && (
        <PlanAnsicht projektId={projekt.id} maengel={maengel.data ?? []} kann={kann}
                     leitung={projekt.meine_rolle === "leitung"} oeffnen={setAuswahl} />
      )}
      {ansicht === "liste" && <div className="liste">
        {maengel.data?.map((m) => (
          <button key={m.id} className="zeile" onClick={() => setAuswahl(m)}>
            <span className="nr">{m.nummer}</span>
            <div className="haupt">
              <div>{m.lage ? `${m.lage}: ` : ""}{m.beschreibung}</div>
              <div className="mute">
                {m.leistung ?? "ohne Leistung"} · festgestellt {datum(m.festgestellt_am)}
                {m.frist && ` · Frist ${datum(m.frist)}`} · {m.vor_abnahme ? "vor Abnahme (§ 4 Abs. 7)" : "nach Abnahme (§ 13 Abs. 5)"}
              </div>
            </div>
            <span style={{ display: "flex", gap: 6 }}>
              {m.plan_id && <span className="chip" title="im Lageplan verortet"><MapPin size={13} /></span>}
              {!!m.fotos && <span className="chip"><Camera size={13} /> {m.fotos}</span>}
              {m.wesentlich && <span className="chip fehler">wesentlich</span>}
              <span className={`chip ${FARBE[m.status]}`}>{MANGEL_STATUS[m.status]}</span>
            </span>
          </button>
        ))}
        {maengel.data?.length === 0 && <div className="leer">{nurOffen ? "Keine offenen Mängel." : "Keine Mängel erfasst."}</div>}
      </div>}
      <NeuerMangel projekt={projekt} leistungen={leistungen.data ?? []} offen={neu} zu={() => setNeu(false)} />
      {auswahl && <MangelDialog mangel={auswahl} kann={kann} zu={() => setAuswahl(null)} />}
    </section>
  );
}

export function NeuerMangel({ projekt, leistungen, offen, zu, begehungId, leistungId, punktId, beschreibung }: {
  projekt: Projekt | { id: string }; leistungen: Leistung[]; offen: boolean; zu: () => void;
  begehungId?: string; leistungId?: string | null;
  /** Aus einem Protokollpunkt erfasst (mangel.punkt_id): der Punkt zeigt danach „M-012". */
  punktId?: string; beschreibung?: string;
}) {
  const qc = useQueryClient();
  const leer = { beschreibung: beschreibung ?? "", lage: "", leistung_id: leistungId ?? "", frist: "", wesentlich: false };
  const [f, setF] = useState(leer);
  // Fotos schon beim Erfassen: auf der Baustelle ist der Mangel und sein Foto EIN Vorgang.
  // Hochgeladen wird erst, wenn der Mangel steht — vorher gibt es kein Ziel.
  const [bilder, setBilder] = useState<File[]>([]);
  const anlegen = useMutation({
    mutationFn: async () => {
      // Das Datum der Erfassung, nicht das der Übertragung — offline kann Tage dazwischen liegen.
      const rumpf = {
        ...f, leistung_id: f.leistung_id || null, frist: f.frist || null, begehung_id: begehungId ?? null,
        punkt_id: punktId ?? null, festgestellt_am: heute(),
      };
      const titel = `${f.lage ? `${f.lage}: ` : ""}${f.beschreibung}`.slice(0, 90);
      let m: Mangel;
      try {
        m = await schick<Mangel>(`/projekte/${projekt.id}/maengel`, rumpf);
      } catch (fehler) {
        // Funkloch auf der Baustelle: im Gerät vormerken (lib/warteschlange.ts).
        if (!ohneNetz(fehler)) throw fehler;
        await vormerken({ art: "mangel", projektId: projekt.id, titel, rumpf, bilder, namen: bilder.map((b) => b.name) });
        return "vorgemerkt" as const;
      }
      if (bilder.length) {
        const d = new FormData();
        bilder.forEach((b) => d.append("dateien", b));
        d.append("ziel", "mangel_id");
        d.append("ziel_id", m.id);
        try {
          await schick(`/projekte/${projekt.id}/fotos`, d);
        } catch (fehler) {
          if (!ohneNetz(fehler)) throw fehler;
          await vormerken({ art: "foto", projektId: projekt.id, titel: `Fotos zu ${m.nummer}`, ziel: "mangel_id",
                            zielId: m.id, bilder, namen: bilder.map((b) => b.name) });
        }
      }
      return m;
    },
    // Vorgemerktes zeigt die Kopfleiste („offline · 1 wartet", components/Netzstand.tsx).
    onSuccess: () => {
      setBilder([]);
      qc.invalidateQueries({ queryKey: ["maengel", projekt.id] });
      if (begehungId) qc.invalidateQueries({ queryKey: ["begehung", begehungId] });
      if (punktId) qc.invalidateQueries({ queryKey: ["besprechung"] });
      setF(leer);
      zu();
    },
  });
  return (
    <Dialog offen={offen} zu={zu} titel="Mangel erfassen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.beschreibung.trim() || anlegen.isPending} onClick={() => anlegen.mutate()}>Erfassen</button>
    </>}>
      <div className="raster">
        <Feld titel="Lage" breit><input className="eingabe" placeholder="z. B. Kletterturm Nord" value={f.lage} onChange={(e) => setF({ ...f, lage: e.target.value })} /></Feld>
        <div className="feld breit">
          <div className="feld-kopf"><span>Beschreibung</span>
            <Diktat text={f.beschreibung} speichern={(v) => setF((x) => ({ ...x, beschreibung: v }))} /></div>
          <textarea className="eingabe" rows={3} autoFocus aria-label="Beschreibung" value={f.beschreibung}
                    onChange={(e) => setF({ ...f, beschreibung: e.target.value })} />
        </div>
        {/* Aus einer Begehung erbt der Mangel deren Leistung — außer sie hat mehrere
            (Wanderung 022): dann kommen hier nur ihre Leistungen zur Wahl. */}
        {(!begehungId || leistungen.length > 0) && (
          <Feld titel="Leistung">
            <Auswahl value={f.leistung_id} onChange={(e) => setF({ ...f, leistung_id: e.target.value })}>
              <option value="">–</option>
              {leistungen.map((l) => <option key={l.id} value={l.id}>{l.bezeichnung}</option>)}
            </Auswahl>
          </Feld>
        )}
        <Feld titel="Frist zur Beseitigung">
          <input type="date" className="eingabe" value={f.frist} onChange={(e) => setF({ ...f, frist: e.target.value })} />
        </Feld>
        <label className="haken breit">
          <input type="checkbox" checked={f.wesentlich} onChange={(e) => setF({ ...f, wesentlich: e.target.checked })} />
          wesentlicher Mangel (berechtigt zur Abnahmeverweigerung)
        </label>
        <label className="feld breit"><span>Fotos</span>
          <input type="file" className="eingabe" accept="image/*,.heic,.heif" multiple
                 onChange={(e) => setBilder(Array.from(e.target.files ?? []))} />
        </label>
        {bilder.length > 0 && <div className="mute breit">{bilder.length} Foto{bilder.length > 1 ? "s" : ""} werden mit dem Mangel hochgeladen.</div>}
      </div>
      <Meldung fehler={anlegen.error} />
    </Dialog>
  );
}

function MangelDialog({ mangel, kann, zu }: { mangel: Mangel; kann: boolean; zu: () => void }) {
  const qc = useQueryClient();
  const voll = useQuery({ queryKey: ["mangel", mangel.id], queryFn: () => lies<Mangel>(`/maengel/${mangel.id}`) });
  const [bemerkung, setBemerkung] = useState("");
  const setzen = useMutation({
    mutationFn: (status: MangelStatus) => aender<Mangel>(`/maengel/${mangel.id}`, { status, bemerkung }),
    onSuccess: (m) => {
      qc.setQueryData(["mangel", mangel.id], m);
      // Der Status steht auch auf der Begehungsseite und zaehlt in den Leistungen mit.
      for (const k of [["maengel"], ["begehung"], ["leistungen"], ["projekte"]]) qc.invalidateQueries({ queryKey: k });
      setBemerkung("");
    },
  });
  const m = voll.data ?? mangel;
  const [verorten, setVerorten] = useState(false);
  if (verorten) return <Verorten mangel={m} zu={() => setVerorten(false)} />;
  return (
    <Dialog offen zu={zu} titel={`${m.nummer} · ${MANGEL_STATUS[m.status]}`} fuss={<>
      {kann && <button className="knopf leise" style={{ marginRight: "auto" }} onClick={() => setVerorten(true)}>
        <MapPin size={16} /> {m.plan_id ? "Verortung ändern" : "Im Plan verorten"}</button>}
      <button className="knopf" onClick={zu}>Schließen</button>
    </>}>
      <p style={{ marginTop: 0 }}>{m.lage && <b>{m.lage}: </b>}{m.beschreibung}</p>
      <FotoLeiste projektId={m.projekt_id} ziel="mangel_id" zielId={m.id} kann={kann}
                  geaendert={() => qc.invalidateQueries({ queryKey: ["maengel"] })} />
      <div className="liste">
        {voll.data?.verlauf?.map((v, i) => (
          <div key={i} className="mute">{datum(v.am)} · {MANGEL_STATUS[v.status]}{v.bemerkung && ` – ${v.bemerkung}`}{v.von_name && ` (${v.von_name})`}</div>
        ))}
      </div>
      {kann && (
        <>
          <Feld titel="Bemerkung zur Änderung">
            <input className="eingabe" value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} />
          </Feld>
          <div className="punkt-leiste">
            {((MANGEL_UEBERGAENGE[m.status] ?? []) as MangelStatus[])
              .map((s) => <button key={s} className="knopf" onClick={() => setzen.mutate(s)}>{MANGEL_STATUS[s]}</button>)}
          </div>
        </>
      )}
      <Meldung fehler={setzen.error} />
    </Dialog>
  );
}
